import { WebRTCBase } from "./base";
import { WebRTC } from "./webrtc";
export class StreamWebRTC extends WebRTC {
    constructor(params, settings) {
        params.peerConfig = params.peerConfig || {};
        params.peerConfig['encodedInsertableStreams'] = true;
        super(params, settings);
        this.modeCode = null;
        this.fps = null;
        // track
        this.video = null;
        // tunnel
        this.canvas = null;
        this.tunnelParams = null;
    }
    doClose() {
        super.doClose();
        this.clearTimeUpdate();
    }
    initVideoTrack(receiver) {
        const streams = receiver.createEncodedStreams();
        if (this.isModeTrack()) {
            streams.readable.pipeTo(streams.writable);
        }
        else if (this.isModeTunnel()) {
            // canvas
            this.canvas.width = this.resolution.width;
            this.canvas.height = this.resolution.height;
            this.canvas.style.aspectRatio = '' + this.canvas.width / this.canvas.height;
            // config
            const config = {
                codec: this.tunnelParams.codec,
                description: this.tunnelParams.description
            };
            // worker
            let worker = this.getWorker(WebRTC.LEGACY_WORKER_ID);
            let canvas = this.canvas.transferControlToOffscreen();
            const trackReadable = streams.readable;
            worker.postMessage({
                type: WebRTCBase.ACTION_DECODE_VIDEO,
                args: { id: "StreamWebRTC", canvas, trackReadable, config, debugging: this.workerDebug, fps: this.fps, frameStats: this.featureSettings?.frameStats }
            }, [canvas, trackReadable]);
        }
    }
    initAudioTrack(receiver) {
        const streams = receiver.createEncodedStreams();
        streams.readable.pipeTo(streams.writable);
    }
    clearTimeUpdate() {
        if (typeof this.timerId === 'number') {
            clearInterval(this.timerId);
            this.timerId = null;
        }
    }
    setTimeUpdate() {
        this.clearTimeUpdate();
        this.timerId = setInterval(() => {
            if (this.isModeTrack()) {
                this.emitTimeUpdate();
            }
            else {
                if (this.workers.size > 0) {
                    const worker = this.workers.values().next().value;
                    worker.postMessage({ type: WebRTCBase.ACTION_GET_TIME, args: { connId: this.connId } });
                }
            }
        }, 200);
    }
    emitTimeUpdate() {
        if (typeof this.currentTime === 'number') {
            this.emitEvent(StreamWebRTC.EVENT_TIME_UPDATE, this.currentTime);
        }
    }
    onMessage(data) {
        if (super.onMessage(data))
            return true;
        switch (data.type) {
            case WebRTCBase.ACTION_GET_TIME_REPLY:
                this.currentTime = data.args.currentTime;
                this.emitTimeUpdate();
                return true;
        }
        return false;
    }
    configureMode() {
        this.peerConn.ontrack = (event) => {
            if (event.track.kind == StreamWebRTC.TRACK_KIND_VIDEO) {
                if (this.isModeTrack()) {
                    this.video.srcObject = event.streams[0];
                }
                this.initVideoTrack(event.receiver);
            }
            else if (event.track.kind == StreamWebRTC.TRACK_KIND_AUDIO) {
                this.audio.srcObject = event.streams[0];
                this.initAudioTrack(event.receiver);
            }
        };
        if (this.isModeTrack()) {
            let callback = (now, metadata) => {
                this.currentTime = WebRTCBase.millisToSeconds(metadata.rtpTimestamp);
                this.video.requestVideoFrameCallback(callback);
            };
            this.video.requestVideoFrameCallback(callback);
        }
        this.setTimeUpdate();
    }
    async onAnswer(params, answer) {
        params.modeCode = StreamWebRTC.MODE_CODE_TUNNEL;
        if (params.modeCode === StreamWebRTC.MODE_CODE_TRACK) {
            if (!params.video)
                throw new Error("The video is required for the track mode");
            this.video = params.video;
        }
        else {
            if (!params.tunnelParams) {
                params.tunnelParams = { codec: "", description: '' };
            }
            if (!params.canvas)
                throw new Error("The canvas is required for the tunnel mode");
            this.canvas = params.canvas;
            this.tunnelParams = params.tunnelParams;
        }
        this.modeCode = params.modeCode;
        this.fps = params.fps;
        if (!params.audio)
            throw new Error("The audio is required");
        this.audio = params.audio;
        this.audio.autoplay = true;
        this.resolution = params.resolution;
        console.info("onAnswer: connId=" + this.connId + ", mode=" + this.getMode()
            + ', resolution=' + JSON.stringify(params.resolution));
        this.configureMode();
        await this.putAnswer(answer);
    }
    static isValidModeCode(modeCode) {
        return Number.isInteger(modeCode) && modeCode >= StreamWebRTC.MODE_CODE_TRACK && modeCode <= StreamWebRTC.MODE_CODE_TUNNEL;
    }
    isModeTrack() {
        return StreamWebRTC.isModeTrack(this.modeCode);
    }
    isModeTunnel() {
        return StreamWebRTC.isModeTunnel(this.modeCode);
    }
    static isModeTrack(modeCode) {
        return modeCode === StreamWebRTC.MODE_CODE_TRACK;
    }
    static isModeTunnel(modeCode) {
        return modeCode === StreamWebRTC.MODE_CODE_TUNNEL;
    }
    getMode() {
        return this.isModeTunnel() ? "tunnel" : "track";
    }
    pause(callback) {
        return this.sendActionMessage(StreamWebRTC.ACTION_PAUSE, {});
    }
    resume(callback) {
        return this.sendActionMessage(StreamWebRTC.ACTION_RESUME, {});
    }
    seek(seek, callback) {
        return this.sendActionMessage(StreamWebRTC.ACTION_SEEK, { seek });
    }
    scale(scale, callback) {
        return this.sendActionMessage(StreamWebRTC.ACTION_SCALE, { scale });
    }
    speed(speed, callback) {
        return this.sendActionMessage(StreamWebRTC.ACTION_SPEED, { speed }, callback);
    }
    ptz(param, callback) {
        const { cmd, token, horizontal_speed = 5, vertical_speed = 5 } = param;
        return this.sendActionMessage(StreamWebRTC.ACTION_PTZ, { cmd, token, horizontal_speed, vertical_speed });
    }
    ptzAreaZoomIn(param, callback) {
        return this.sendActionMessage(StreamWebRTC.ACTION_PTZ_AREA_ZOOM_IN, param, callback);
    }
}
StreamWebRTC.MODE_CODE_TRACK = 1;
StreamWebRTC.MODE_CODE_TUNNEL = 2;
StreamWebRTC.TRACK_KIND_VIDEO = "video";
StreamWebRTC.TRACK_KIND_AUDIO = "audio";
// actions
StreamWebRTC.ACTION_PAUSE = 'stream.pause';
StreamWebRTC.ACTION_RESUME = 'stream.resume';
StreamWebRTC.ACTION_SEEK = 'stream.seek';
StreamWebRTC.ACTION_SCALE = 'stream.scale';
StreamWebRTC.ACTION_SPEED = 'stream.speed';
StreamWebRTC.ACTION_PTZ = 'ptz.ctrl';
StreamWebRTC.ACTION_PTZ_AREA_ZOOM_IN = 'ptz.area.zoom_in';
StreamWebRTC.ACTION_OFFER = 'signal.offer';
// events
StreamWebRTC.EVENT_TIME_UPDATE = 'time_update';
